import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, Loader2, Crown, Sparkles, Wallet, Plus, Tag, ShieldCheck, X, Gift, Clock, ShoppingCart } from "lucide-react";
import { AddItems } from "@/components/IndividualHub";
import api, { formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { CheckoutDialog } from "@/components/CheckoutDialog";
import { LiabilityConsentDialog } from "@/components/LiabilityConsent";
import { PaymentSoonDialog } from "@/components/PaymentSoonDialog";
import { InvoicesList } from "@/components/InvoicesList";
import { usePricing, ServicesTable, FeatureMatrix, QuotaPolicy } from "@/components/PricingDetails";

const RES_AR = { projects: "المشاريع", strategies: "الاستراتيجيات", campaigns: "الحملات", images: "الصور", videos: "الفيديوهات" };
const TOPUP_PRESETS = [25, 50, 100, 250, 500];

function UsageBar({ label, used, limit }) {
  const unlimited = limit === -1;
  const pct = unlimited ? 12 : Math.min(100, Math.round((used / limit) * 100));
  return (
    <div data-testid={`usage-${label}`}>
      <div className="flex justify-between text-xs mb-1">
        <span className="text-muted-foreground">{RES_AR[label]}</span>
        <span className="font-semibold">{used} / {unlimited ? "حسب الطلب" : limit}</span>
      </div>
      <div className="h-2 rounded-full bg-secondary overflow-hidden">
        <div className={`h-full rounded-full ${pct >= 100 ? "bg-destructive" : "bg-primary"}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function WalletCard({ wallet, onTopup, topping }) {
  const [custom, setCustom] = useState("");
  return (
    <div className="glass rounded-2xl p-6 border border-accent/30" data-testid="wallet-card">
      <div className="flex items-center gap-2 mb-1">
        <Wallet className="w-5 h-5 text-accent" />
        <h2 className="font-bold">محفظة الدفع حسب الطلب</h2>
      </div>
      <p className="text-xs text-muted-foreground mb-3">
        {wallet.overage_pay_per_use
          ? "اشحن رصيدك: أي خدمة إضافية فوق المشمول في باقتك تُخصم من المحفظة بتكلفتها عند الاستخدام. الأسعار تظهر عند اختيار الخدمة."
          : "اشحن رصيدك: تُخصم تكلفة كل خدمة من المحفظة عند استخدامها أو شرائها. الأسعار تظهر عند اختيار الخدمة."}
      </p>
      <div className="flex items-end justify-between mb-4">
        <div>
          <div className="text-xs text-muted-foreground">الرصيد الحالي</div>
          <div className="text-3xl font-extrabold text-accent" data-testid="wallet-balance">{wallet.balance} <span className="text-sm text-muted-foreground">ريال</span></div>
        </div>
      </div>
      <p className="text-[11px] text-muted-foreground mb-1">شحن الرصيد:</p>
      <div className="flex flex-wrap gap-2 mb-3">
        {TOPUP_PRESETS.map((a) => (
          <Button key={a} size="sm" variant="outline" disabled={topping} onClick={() => onTopup(a)}
            className="border-accent/40 text-accent gap-1" data-testid={`topup-${a}`}>
            <Plus className="w-3 h-3" /> {a}
          </Button>
        ))}
      </div>
      <div className="flex gap-2">
        <Input type="number" value={custom} onChange={(e) => setCustom(e.target.value)} placeholder="مبلغ مخصص" dir="ltr"
          className="bg-secondary border-border h-9 text-sm" data-testid="topup-custom-input" />
        <Button size="sm" disabled={topping || !custom} onClick={() => { onTopup(Number(custom)); setCustom(""); }}
          className="bg-accent hover:bg-accent/90 text-accent-foreground" data-testid="topup-custom-btn">
          {topping ? <Loader2 className="w-4 h-4 animate-spin" /> : "شحن"}
        </Button>
      </div>
      <p className="text-[11px] text-muted-foreground mt-3">الدفع الإلكتروني الآمن سيتوفر قريباً، وتصلك فاتورة على إيميلك.</p>
    </div>
  );
}

export default function Packages() {
  const { user, setUser } = useAuth();
  const [plans, setPlans] = useState([]);
  const [usage, setUsage] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(null);
  const [topping, setTopping] = useState(false);
  const [couponInput, setCouponInput] = useState("");
  const [coupon, setCoupon] = useState(null); // {code, discount_percent}
  const [validating, setValidating] = useState(false);

  const applyCoupon = async () => {
    const code = couponInput.trim();
    if (!code) return;
    setValidating(true);
    try {
      const { data } = await api.post("/coupons/validate", { code });
      setCoupon({ code: data.code, discount_percent: data.discount_percent });
      toast.success(`تم تفعيل كوبون «${data.code}» — خصم ${data.discount_percent}%`);
    } catch (err) {
      setCoupon(null);
      toast.error(formatError(err.response?.data?.detail) || "كود غير صالح");
    } finally { setValidating(false); }
  };

  const clearCoupon = () => { setCoupon(null); setCouponInput(""); };

  const discounted = (price) => {
    if (!coupon || !price) return price;
    return Math.round(price * (100 - coupon.discount_percent) / 100);
  };

  const pricing = usePricing();
  const [payCfg, setPayCfg] = useState({ enabled: false });
  const [promo, setPromo] = useState(null);
  const [soon, setSoon] = useState(false);
  const load = () => {
    Promise.all([api.get("/plans"), api.get("/usage"), api.get("/wallet"), api.get("/payments/config"), api.get("/coupons/promo")])
      .then(([p, u, w, c, pr]) => {
        const sorted = [...p.data].sort((a, b) => (a.price || 0) - (b.price || 0));
        setPlans(sorted); setUsage(u.data); setWallet(w.data); setPayCfg(c.data); setPromo(pr.data);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  useEffect(load, []);
  useEffect(() => { if (!loading && window.location.hash === "#credits") setTimeout(() => document.getElementById("credits")?.scrollIntoView({ behavior: "smooth", block: "start" }), 150); }, [loading]);

  const [checkout, setCheckout] = useState(null);
  const activateTrial = async (code) => {
    setSubscribing("trial");
    try {
      await api.post("/subscribe", { plan: "trial", coupon: code, accepted_liability: true });
      const me = await api.get("/auth/me");
      setUser(me.data); setSoon(false); load();
      toast.success("تم تفعيل التجربة المجانية لمدة شهر 🎉");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally { setSubscribing(null); }
  };

  const isAdmin = user?.role === "admin";
  const [consentFor, setConsentFor] = useState(null);
  const subscribe = async (planId, agreed = false) => {
    const plan = plans.find((x) => x.id === planId);
    if (!isAdmin) {
      if (!payCfg.enabled && plan?.price > 0) return setSoon(true);
      const due = discounted(plan?.price || 0);
      if (due > 0) return setCheckout({ kind: "plan", plan: planId, coupon: coupon?.code || null, amount: due, title: `اشتراك باقة ${plan.name} لمدة شهر` });
      if (planId !== "free" && !agreed) return setConsentFor(planId);  // إقرار قبل بدء أي خدمة
    }
    setSubscribing(planId);
    try {
      await api.post("/subscribe", { plan: planId, coupon: isAdmin ? null : coupon?.code || null, accepted_liability: true });
      const me = await api.get("/auth/me");
      setUser(me.data);
      load();
      toast.success(isAdmin ? "تم التبديل للباقة (وضع المشرف) ✅" : "تم تفعيل الباقة بنجاح 🎉");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setSubscribing(null);
    }
  };

  const topup = (amount) => {
    if (!payCfg.enabled) return setSoon(true);
    if (!amount || amount < 1 || amount > 5000) return toast.error("المبلغ بين 1 و 5000 ريال");
    setCheckout({ kind: "wallet", amount, title: `شحن المحفظة بمبلغ ${amount} ريال` });
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  const current = user?.plan || "free";
  const isTrial = usage?.plan?.trial;
  const usageKeys = isTrial ? ["images", "videos"] : wallet?.pay_per_use ? ["projects", "strategies"]
    : wallet?.overage_pay_per_use ? ["projects", "images", "videos"]
    : ["projects", "strategies", "campaigns", "images", "videos"];

  return (
    <div className="space-y-8" data-testid="packages-page">
      <div>
        <h1 className="text-3xl font-extrabold font-heading">الباقات والاشتراك</h1>
        <p className="text-muted-foreground">اختر الباقة المناسبة لنمو مشروعك — أسعار بالريال السعودي.</p>
      </div>
      {isAdmin && (
        <div className="rounded-2xl border border-accent/40 bg-accent/10 p-4 flex items-center gap-3 flex-wrap" data-testid="admin-plan-switcher">
          <ShieldCheck className="w-5 h-5 text-accent shrink-0" />
          <div className="text-sm flex-1 min-w-[220px]">
            <b>وضع المشرف:</b> كل الباقات مفعّلة لك بدون دفع — بدّل بينها لاختبار تجربة كل باقة.
            <span className="text-xs text-muted-foreground block mt-0.5">الباقة الحالية: <b className="text-foreground">{usage?.plan?.name || current}</b> · صلاحيات المشرف تبقى بلا حدود في كل الأحوال.</span>
          </div>
          <div className="flex gap-2 flex-wrap">
            {[["trial", "التجربة"], ["free", "المجانية"], ["enterprise", "المؤسسات"]].map(([id, name]) => (
              <Button key={id} size="sm" variant="outline" disabled={current === id || subscribing === id} onClick={() => subscribe(id)}
                className="border-accent/40" data-testid={`admin-switch-${id}`}>{current === id ? `✓ ${name}` : name}</Button>
            ))}
          </div>
        </div>
      )}

      {!payCfg.enabled && (
        <div className="rounded-2xl p-5 border border-accent/50 bg-accent/5 flex items-center justify-between flex-wrap gap-4" data-testid="payment-soon-banner">
          <div className="flex items-start gap-3">
            <Clock className="w-6 h-6 text-accent shrink-0 mt-0.5" />
            <div>
              <div className="font-bold">الدفع الإلكتروني سيتوفر قريباً</div>
              <p className="text-sm text-muted-foreground">
                جرّب رواج الذكية الآن مجاناً لمدة شهر — صورتان + فيديو واحد في مسار «محتوى متنوع»
                {promo?.code && <> بالكوبون <b className="text-accent tracking-wider" dir="ltr" data-testid="promo-code">{promo.code}</b></>}.
              </p>
            </div>
          </div>
          <Button onClick={() => setSoon(true)} disabled={isTrial} className="font-bold gap-2" data-testid="open-trial-btn">
            <Gift className="w-4 h-4" /> {isTrial ? "التجربة مفعّلة" : "فعّل التجربة المجانية"}
          </Button>
        </div>
      )}

      {/* Coupon */}
      {payCfg.enabled && (
      <div className="glass rounded-2xl p-5 border border-accent/30" data-testid="coupon-box">
        <div className="flex items-center gap-2 mb-3">
          <Tag className="w-5 h-5 text-accent" />
          <h2 className="font-bold">كوبون الخصم</h2>
        </div>
        {coupon ? (
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="text-sm">
              كوبون مُفعّل: <b className="text-accent" data-testid="active-coupon">{coupon.code}</b> — خصم <b>{coupon.discount_percent}%</b> على كل الباقات
            </div>
            <Button size="sm" variant="outline" onClick={clearCoupon} className="gap-1 border-border" data-testid="clear-coupon-btn">
              <X className="w-4 h-4" /> إزالة
            </Button>
          </div>
        ) : (
          <div className="flex gap-2 flex-wrap">
            <Input value={couponInput} onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
              placeholder="أدخل كود الكوبون (مثال: FREEMONTH)" dir="ltr"
              className="bg-secondary border-border h-10 text-sm flex-1 min-w-[200px] text-start" data-testid="coupon-input" />
            <Button onClick={applyCoupon} disabled={validating || !couponInput.trim()}
              className="bg-accent hover:bg-accent/90 text-accent-foreground gap-1" data-testid="apply-coupon-btn">
              {validating ? <Loader2 className="w-4 h-4 animate-spin" /> : "تفعيل"}
            </Button>
          </div>
        )}
      </div>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        {usage && (
          <div className="lg:col-span-2 glass rounded-2xl p-6 border border-primary/30" data-testid="current-usage">
            <div className="flex items-center gap-2 mb-4">
              <Crown className="w-5 h-5 text-accent" />
              <h2 className="font-bold">باقتك الحالية: <span className="text-primary">{usage.plan.name}</span></h2>
              {usage.plan_expires_at && usage.plan.id !== "free" && (
                <span className="text-xs text-muted-foreground ms-auto" data-testid="plan-expiry">تنتهي في {usage.plan_expires_at.slice(0, 10)}</span>
              )}
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              {usageKeys.map((k) => (
                <UsageBar key={k} label={k} used={usage.usage[k]} limit={usage.plan.limits[k]} />
              ))}
            </div>
          </div>
        )}
        {(wallet?.pay_per_use || wallet?.overage_pay_per_use) && <WalletCard wallet={wallet} onTopup={topup} topping={topping} />}
      </div>

      <section id="credits" className="rounded-2xl border border-accent/40 bg-card p-5 space-y-3 scroll-mt-28" data-testid="packages-credits-section">
        <div>
          <h2 className="text-base md:text-lg font-bold flex items-center gap-2"><ShoppingCart className="w-5 h-5 text-accent" /> خدمات حسب الطلب — بدون اشتراك</h2>
          <p className="text-xs text-muted-foreground mt-1">اختر الخدمة والكمية، وادفع حسب احتياجك دون اشتراك شهري. تُضاف الخدمات المشتراة إلى رصيدك لاستخدامها لاحقًا وفق الشروط الموضحة.</p>
        </div>
        {wallet && <AddItems wallet={wallet} selected={new URLSearchParams(window.location.search).get("kind") || ""} onPick={() => {}} onChanged={load} onPay={(amount) => (payCfg.enabled ? setCheckout({ kind: "wallet", amount, title: `شراء خدمات بقيمة ${amount} ريال` }) : setSoon(true))} />}
        <details className="group" data-testid="services-scope-details">
          <summary className="cursor-pointer text-xs text-primary underline underline-offset-2 list-none">نطاق كل خدمة وسعرها — اطّلع قبل الشراء</summary>
          <div className="mt-3"><ServicesTable pricing={pricing} /></div>
        </details>
      </section>

      <section className="space-y-3" data-testid="plans-matrix-section">
        <h2 className="text-base md:text-lg font-bold">ماذا تشمل كل باقة؟</h2>
        <FeatureMatrix pricing={pricing} />
        <QuotaPolicy pricing={pricing} />
      </section>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 items-stretch">
        {plans.map((p) => {
          const isCurrent = p.id === current;
          return (
            <div key={p.id} data-testid={`plan-card-${p.id}`}
              className={`rounded-3xl p-6 border flex flex-col glass ${p.popular ? "border-primary/60 emerald-glow" : "border-border"}`}>
              {p.popular && <div className="inline-flex items-center gap-1 bg-primary text-white text-xs font-bold px-3 py-1 rounded-full mb-3 self-start"><Sparkles className="w-3 h-3" /> الأكثر طلباً</div>}
              <h3 className="text-xl font-bold">{p.name}</h3>
              <div className="my-4">
                {p.price === 0 ? (
                  <span className="text-3xl font-extrabold">مجاناً</span>
                ) : coupon && coupon.discount_percent >= 100 ? (
                  <>
                    <span className="text-lg text-muted-foreground line-through me-2">{p.price}</span>
                    <span className="text-3xl font-extrabold text-accent" data-testid={`discounted-${p.id}`}>مجاناً</span>
                    <span className="block text-[11px] text-accent mt-1">🎁 شهر مجاني بالكامل عبر الكوبون</span>
                  </>
                ) : coupon ? (
                  <>
                    <span className="text-lg text-muted-foreground line-through me-2">{p.price}</span>
                    <span className="text-3xl font-extrabold text-accent" data-testid={`discounted-${p.id}`}>{discounted(p.price)}</span>
                    <span className="text-xs text-muted-foreground"> {p.currency} · {p.period}</span>
                    <span className="block text-[11px] text-accent mt-1">بعد خصم {coupon.discount_percent}%</span>
                  </>
                ) : (
                  <><span className="text-3xl font-extrabold">{p.price}</span> <span className="text-xs text-muted-foreground">{p.currency} · {p.period}</span>{pricing?.vat_pct ? <span className="block text-[11px] text-muted-foreground mt-1" data-testid={`plan-vat-${p.id}`}>+ ضريبة القيمة المضافة {pricing.vat_pct}% عند الدفع</span> : null}</>
                )}
              </div>
              <ul className="space-y-2 mb-6 flex-1">
                {p.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm"><Check className="w-4 h-4 text-primary shrink-0 mt-0.5" /> {f}</li>
                ))}
              </ul>
              <Button
                onClick={() => subscribe(p.id)}
                disabled={isCurrent || subscribing === p.id}
                data-testid={`subscribe-${p.id}`}
                className={`w-full font-bold ${isCurrent ? "bg-secondary text-muted-foreground" : p.popular ? "bg-primary hover:bg-primary/90 text-white" : "bg-secondary hover:bg-secondary/80"}`}>
                {subscribing === p.id ? <Loader2 className="w-4 h-4 animate-spin" />
                  : isCurrent ? "باقتك الحالية"
                  : isAdmin ? "تفعيل فوري (مشرف)"
                  : p.price === 0 ? "التفعيل المجاني"
                  : !payCfg.enabled ? "الاشتراك قريباً"
                  : coupon && coupon.discount_percent >= 100 ? "فعّل مجاناً لمدة شهر"
                  : `اشترك الآن — ${discounted(p.price)} ريال`}
              </Button>
            </div>
          );
        })}
      </div>
      <div className="glass rounded-2xl p-5 border border-border flex items-start gap-3" data-testid="payment-policy-note">
        <ShieldCheck className="w-5 h-5 text-primary shrink-0 mt-0.5" />
        <p className="text-sm text-muted-foreground leading-relaxed">
          جميع العمليات المالية تتم عبر <b className="text-foreground">بوابة دفع إلكترونية آمنة</b> ومشفّرة، وتخضع لـ
          <Link to="/terms" className="text-primary hover:underline mx-1">الشروط والأحكام</Link>،
          <Link to="/privacy" className="text-primary hover:underline mx-1">سياسة الخصوصية</Link>،
          <Link to="/refund" className="text-primary hover:underline mx-1">سياسة الاسترجاع/الاسترداد</Link>، و
            <Link to="/security" className="text-primary hover:underline mx-1">سياسة الأمان</Link>.
          <span className="block mt-1 text-xs">بيانات البطاقة تُدخل في صفحة بوابة الدفع المعتمدة المشفّرة فقط، ولا تمر على خوادم رواج الذكية. تصلك فاتورة مفصلة على إيميلك بعد كل عملية.</span>
        </p>
      </div>
      <InvoicesList />
      {soon && <PaymentSoonDialog promoCode={promo?.code} isTrial={isTrial} busy={subscribing === "trial"} onActivate={activateTrial} onClose={() => setSoon(false)} />}
      {checkout && <CheckoutDialog order={checkout} onClose={() => setCheckout(null)} />}
      <LiabilityConsentDialog open={!!consentFor} onClose={() => setConsentFor(null)} onAccept={() => { const id = consentFor; setConsentFor(null); subscribe(id, true); }} />
    </div>
  );
}
